/**
 * Minimap Component
 *
 * A video-game-style minimap showing a bird's-eye view of the flowchart
 * with a viewport indicator that can be dragged to navigate.
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Tiles and wires are one canvas, repainted when the graph changes
 * - Viewport uses CSS transform (GPU accelerated) instead of left/top
 * - Viewport calculation uses refs to avoid re-renders
 */

import React, { useRef, useMemo, memo } from 'react';
import './Minimap.css';

import {
  useMinimapSize,
  useMinimapGeometry,
  useMinimapViewport,
  useMinimapPanInteraction,
  useMinimapCanvas,
} from './hooks';
import { getContentBounds } from './utils/getContentBounds';
import { areMinimapPropsEqual } from './utils/areMinimapPropsEqual';

const Minimap = ({
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  pan,
  zoom,
  containerRef,
  contentBounds: externalBounds,
  onPanChange,
  onPanChangeAnimated,
  constrainPan,
  allCategories = [],
  allTags = [],
  lockStates = {},
  statuses = [],
  nestedSprints = [],
  minimapSize,
  onMinimapSizeChange,
  campaignId,
}) => {
  const minimapRef = useRef(null);

  const bounds = useMemo(
    () => getContentBounds(externalBounds),
    [externalBounds],
  );

  const { maxDimensions, isResizing, handleResizeStart } = useMinimapSize(
    containerRef,
    bounds.width,
    bounds.height,
    minimapSize,
    onMinimapSizeChange,
    campaignId,
  );

  const { minimapWidth, minimapHeight, scale } = useMinimapGeometry(
    bounds,
    maxDimensions,
  );

  const { viewportRef, containerSizeRef, panRef, zoomRef } = useMinimapViewport({
    pan,
    zoom,
    containerRef,
    bounds,
    scale,
  });

  const { handleMouseDown } = useMinimapPanInteraction({
    minimapRef,
    containerRef,
    containerSizeRef,
    panRef,
    zoomRef,
    constrainPan,
    bounds,
    scale,
    minimapWidth,
    minimapHeight,
    onPanChange,
    onPanChangeAnimated,
  });

  const bitmapRef = useRef(null);

  useMinimapCanvas({
    canvasRef: bitmapRef,
    tasks,
    nodePositions,
    nodeDimensions,
    edges,
    scale,
    bounds,
    minimapWidth,
    minimapHeight,
    allCategories,
    allTags,
    lockStates,
    statuses,
    nestedSprints,
  });

  if (tasks.length === 0) return null;

  return (
    <div className={`flowchart-minimap ${isResizing ? 'flowchart-minimap--resizing' : ''}`}>
      {/* Corner frame — same JARVIS treatment as mission HUD lists */}
      <span className="flowchart-minimap__frame" aria-hidden="true" />

      <div
        className="flowchart-minimap__resize-handle flowchart-minimap__resize-handle--left"
        onMouseDown={(e) => handleResizeStart('left', e)}
      />

      <div
        className="flowchart-minimap__resize-handle flowchart-minimap__resize-handle--top"
        onMouseDown={(e) => handleResizeStart('top', e)}
      />

      <div
        ref={minimapRef}
        className="flowchart-minimap__canvas"
        style={{
          width: minimapWidth,
          height: minimapHeight,
        }}
        onMouseDown={handleMouseDown}
      >
        <canvas ref={bitmapRef} className="flowchart-minimap__bitmap" />

        <div ref={viewportRef} className="flowchart-minimap__viewport" />
      </div>
    </div>
  );
};

export default memo(Minimap, areMinimapPropsEqual);
